import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "gpp_cookie_consent_v1";

/**
 * CCPA / CPRA-friendly cookie & privacy preference banner.
 * - Stores choice in localStorage so it only appears once.
 * - "Reject optional" defaults preserve only strictly necessary cookies.
 * - Surfaces a "Do Not Sell or Share My Personal Information" link for California residents.
 */
export default function CookieConsent() {
    const { t } = useTranslation();
    const reduce = useReducedMotion();
    const [visible, setVisible] = React.useState(false);

    React.useEffect(() => {
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY);
            if (!stored) {
                // Wait a beat before showing so it doesn't fight with the hero load
                const id = window.setTimeout(() => setVisible(true), 800);
                return () => window.clearTimeout(id);
            }
        } catch (_e) {
            // localStorage blocked - still show the banner each visit so we stay compliant
            setVisible(true);
        }
    }, []);

    const persist = (value) => {
        try {
            window.localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ value, ts: new Date().toISOString() })
            );
        } catch (_e) {
            // ignore
        }
    };

    const handleAccept = () => {
        persist("accept-all");
        setVisible(false);
    };

    const handleReject = () => {
        persist("reject-optional");
        setVisible(false);
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={reduce ? false : { y: 24, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={reduce ? { opacity: 0 } : { y: 24, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    role="region"
                    aria-label={t("cookieBanner.title")}
                    data-testid="cookie-consent-banner"
                    className="fixed left-3 right-3 sm:left-6 sm:right-6 lg:left-auto lg:right-6 bottom-3 sm:bottom-6 z-[200] lg:max-w-md"
                >
                    <div className="rounded-2xl border border-[#E9D9C1] bg-[#FBF7EE]/95 backdrop-blur-md shadow-[0_18px_60px_rgba(6,42,30,0.18)] p-4 sm:p-5">
                        <div className="flex items-start gap-3">
                            <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#F4EBDD] border border-[#E9D9C1] text-[#8B6F2A]">
                                <Cookie className="h-5 w-5" />
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-3">
                                    <h2 className="text-sm sm:text-base font-bold text-[#062A1E]" data-testid="cookie-consent-title">
                                        {t("cookieBanner.title")}
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={handleReject}
                                        aria-label="Dismiss"
                                        data-testid="cookie-consent-dismiss"
                                        className="text-[#1B2621]/60 hover:text-[#062A1E] transition-colors duration-150"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                <p className="mt-1.5 text-xs sm:text-sm leading-relaxed text-[#1B2621]/80">
                                    {t("cookieBanner.body")}
                                </p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <Button
                                        type="button"
                                        onClick={handleAccept}
                                        data-testid="cookie-consent-accept"
                                        className="h-9 px-4 rounded-lg bg-[#0B4A34] hover:bg-[#0F6244] text-[#FBF7EE] text-xs sm:text-sm font-semibold shadow-[0_8px_20px_rgba(6,42,30,0.18)]"
                                    >
                                        {t("cookieBanner.accept")}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={handleReject}
                                        data-testid="cookie-consent-reject"
                                        className="h-9 px-4 rounded-lg bg-[#F4EBDD] hover:bg-[#E9D9C1] text-[#062A1E] text-xs sm:text-sm font-semibold border border-[#E9D9C1]"
                                    >
                                        {t("cookieBanner.reject")}
                                    </Button>
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs">
                                    <Link
                                        to="/do-not-sell"
                                        className="text-[#0B4A34] hover:text-[#137A55] underline underline-offset-2 font-semibold"
                                        data-testid="cookie-consent-ccpa-link"
                                    >
                                        {t("cookieBanner.ccpaLink")}
                                    </Link>
                                    <span className="text-[#1B2621]/40">·</span>
                                    <Link
                                        to="/privacy"
                                        className="text-[#1B2621]/70 hover:text-[#062A1E] underline underline-offset-2"
                                        data-testid="cookie-consent-learn-more"
                                    >
                                        {t("cookieBanner.learnMore")}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
